/**
 * Which rows of a long list are worth touching.
 *
 * `ListBox`, `ListView` and `DataGrid` each worked this out inline, in three
 * spellings of the same arithmetic. That was survivable while the only
 * consumer was `onPaint` - a row drawn one too few is a row nobody misses at
 * the very edge of a viewport. It stops being survivable once the
 * accessibility mirror reads the same window, because then the two can drift
 * and a screen reader describes rows that are not on screen while missing one
 * that is.
 */
export interface RowRange {
  first: number;
  /** Inclusive. `last < first` means the range is empty. */
  last: number;
}

/** No rows. Spelled once so a caller can compare against something. */
export const EMPTY_ROW_RANGE: RowRange = { first: 0, last: -1 };

/**
 * The rows overlapping a viewport, inclusive at both ends.
 *
 * `padStart` is dead space before the first row - a list's top padding. A
 * header is not padding and does not belong here: it does not move with the
 * scroll, so a control with one passes the body height as `viewportExtent`
 * and leaves `padStart` at zero.
 *
 * `overscan` widens the window without changing what it means, for a mirror
 * that would rather build a few nodes early than rebuild the lot on every
 * scrolled pixel.
 */
export function visibleRowRange(
  scrollOffset: number,
  viewportExtent: number,
  rowHeight: number,
  padStart: number,
  count: number,
  overscan = 0,
): RowRange {
  // A zero row height is a control mid-layout, before a theme has given it
  // metrics. Dividing by it yields Infinity, and `Math.floor(Infinity)` is
  // not an index - it is a loop that never ends.
  if (count <= 0 || rowHeight <= 0 || viewportExtent <= 0) return { ...EMPTY_ROW_RANGE };

  const first = Math.max(0, Math.floor((scrollOffset - padStart) / rowHeight) - overscan);
  const last = Math.min(
    count - 1,
    Math.ceil((scrollOffset + viewportExtent - padStart) / rowHeight) + overscan,
  );
  // Scrolled clean past the end - which happens for a frame when a list is
  // emptied while it is scrolled down - leaves first above last, and the
  // caller's `for (i = first; i <= last; i++)` correctly does nothing.
  return { first, last };
}

/** How many rows a range covers. */
export function rowRangeCount(range: RowRange): number {
  return Math.max(0, range.last - range.first + 1);
}
